import Layout from '@/components/layout/Layout';
import { FC } from 'react';
import Link from 'next/link';
import BookInfo from '@/components/ui/book-info/BookInfo';
import BookBreadcrumbs from '@/components/ui/book-breadcrumbs/BookBreadcrumbs';
import AdvancedSearchBar from '@/components/ui/advanced-search-bar/AdvancedSearchBar';
import ReviewsList from '@/components/ui/reviews-list/ReviewsList';
import LitresReader from '@/components/ui/litres-reader/LitresReader';
import FormReview from '@/components/ui/form-review/FormReview';
import ScrollCardsList from '@/components/ui/scroll-cards-list/ScrollCardsList';
import EbookJsonMarkup from '@/components/ui/ebook-json-markup/EbookJsonMarkup';
import { getAuthorFullName } from '@/helpers/helpers';
import { useGetUrlPath } from '@/hooks/useGetUrlPath';
import { IBookDetailData } from '@/interfaces/bookDetail.interface';
import { IReviews } from '@/interfaces/reviews.interface';
import { IQuotes } from '@/interfaces/quotes.interface';
import { ISimilarBooks } from '@/interfaces/similarBooks.interface';
import ScrollCardItem from '@/components/ui/scroll-card-item/ScrollCardItem';
import styles from './BookDetail.module.scss';
import BlogBooks from '@/components/ui/blog-books/BlogBooks';
import BlogBanner from '@/components/ui/banners/blog-banner/BlogBanner';
import { IBlog } from '@/interfaces/blog.interface';
import BlogItemsList from './blog-items-list/BlogItemsList';

interface IBookDetailPage {
  book: IBookDetailData;
  reviews: IReviews;
  quotes: IQuotes;
  similarBooks: ISimilarBooks;
  blogs?: IBlog;
}

const BookDetail: FC<IBookDetailPage> = ({ book, reviews, quotes, similarBooks, blogs }) => {
  console.log(book);

  const bookData = book.book;
  const { first_name, middle_name, last_name } = book.author;

  const authorFullName = getAuthorFullName(first_name, middle_name, last_name);


  const getBookMetaTitle = () =>
    book.book.type === 'audio_book'
      ? `Aудиокнига ${book.book.title}, ${authorFullName} — слушать онлайн или скачать в mp3 (мп3)`
      : `Книга ${book.book.title}, ${authorFullName} — скачать онлайн в pdf, epub, fb2, txt бесплатно в электронной библиотеке Fantasy Worlds.`;

  const getBookMetaDescription = () =>
    book.book.type === 'audio_book'
      ? `Аудиокнига ${book.book.title} - слушать онлайн или скачать в mp3 (мп3), ${authorFullName}. В электронной библиотеке Fantasy Worlds.`
      : `Книга ${book.book.title}, 🕮 ${authorFullName} - ▶ Скачать книгу 📚 онлайн в pdf, epub, fb2, txt. ▶ Аннотация, рейтинг и отзывы в электронной библиотеке Fantasy Worlds.`;

  const SEO_TITLE = getBookMetaTitle();
  const SEO_DESCRIPTION = getBookMetaDescription();
  const SEO_CANONICAL = useGetUrlPath();
  const SEO_COVER = `https://fantasy-worlds.ru/covers/${bookData.external_id}.jpg`;


  const createMarkup = (c: string) => {
    return { __html: c };
  };

  const hideLitresWidgets = book.book.webtoon || book.hide_litres_data || book.book.allow_full_free === 1;

  return (
    <Layout title={SEO_TITLE} description={SEO_DESCRIPTION} canonicalHref={SEO_CANONICAL} cover={SEO_COVER}>
      <EbookJsonMarkup book={book} authorFullName={authorFullName} reviews={reviews} />
      <BookBreadcrumbs bookTitle={book.book.title} bookId={book.id} authorFullName={authorFullName} authorId={book.author.id} genres={book.genres} />
      <AdvancedSearchBar />

      <BookInfo book={book} reviewsCount={reviews.total_count} hideLitresWidgets={hideLitresWidgets} />

      {book.retelling_text && (
        <div className={`${styles.bookDescription} ${styles.bookDescription_retelling}`}>
          {book.book.description !== null && book.book.description.length > 1 && (
            <>
              <div className={styles.bookDescription__title}>Краткий пересказ:</div>
              <p className={styles.bookDescription__txt} dangerouslySetInnerHTML={createMarkup(book.retelling_text)}></p>
            </>
          )}
        </div>
      )}

      {reviews.data.length > 0 && (
        <>
          <h2 className={styles.h2}>Отзывы</h2>
          <ReviewsList reviews={reviews} />
        </>
      )}

      <div id="bookReviewForm">
        <h2 className={styles.h2}>Оcтавить отзыв</h2>
        <FormReview bookId={book.id} bookAlias={book.alias} />
      </div>

      {quotes.data.length > 0 && (
        <>
          <h2 className={styles.h2}>Цитаты</h2>
          <div className={styles.bookQuotes}>
            <ScrollCardsList>
              {quotes.data.map((quote) => (
                <div className={styles.bookQuotes__item}>{quote.text}</div>
              ))}
            </ScrollCardsList>
          </div>
        </>
      )}

      {bookData.type === 'book' && !hideLitresWidgets && (
        <div className={styles.readOnline}>
          <h2 className={styles.h2}>Читать онлайн</h2>
          <LitresReader externalId={bookData.external_id} />
        </div>
      )}

      {/* <BlogBooks title="Статьи о книге" /> */}

      {!!book.other_author_books && book.other_author_books.length > 0 && (
        <div className={styles.scrollBooks}>
          <div className={styles.scrollBooks__title}>
            <h2 className={styles.h2}>Другие книги автора</h2>
            <Link prefetch={false} className={styles.scrollBooks__titleBtn} href={`/author/id${book.author.id}`}>
              Все книги
            </Link>
          </div>
          <ScrollCardsList>
            {book.other_author_books.map((item, i) => {
              const bookAlias = `/lib/id${item.alias}`;
              return <ScrollCardItem key={`${item.id}-${i}`} externalId={item.book.external_id} bookAlias={bookAlias} authorFullName={authorFullName} title={item.book.title} />;
            })}
          </ScrollCardsList>
        </div>
      )}
      {!!similarBooks && similarBooks.length > 0 && (
        <div className={styles.scrollBooks}>
          <div className={styles.scrollBooks__title}>
            <h2 className={styles.h2}>Похожие книги</h2>
          </div>
          <ScrollCardsList>
            {similarBooks.map((item, i) => {
              const bookAlias = `/lib/id${item.alias}`;
              return <ScrollCardItem key={`${item.external_id}-${i}`} externalId={item.external_id} bookAlias={bookAlias} title={item.title} />;
            })}
          </ScrollCardsList>
        </div>
      )}
      {/* <BlogBanner />
      {blogs?.data && blogs.data.length > 0 && <BlogItemsList blogs={blogs} />} */}
    </Layout>
  );
};

export default BookDetail;
